/**
 * The Project tab's data layer: TanStack Query over the ts-rest client.
 *
 * Key shape, and why it changed. The old singleton key `["project","current"]`
 * sat in the same namespace as the sub-resource keys `["project", id, …]`, so
 * `invalidateQueries({ queryKey: ["project"] })` hit both and there was no way
 * to invalidate one project's data without touching the singleton — which is
 * why `useActivateProject` resorted to a bare `invalidateQueries()` that
 * cleared the entire app's cache. Now:
 *
 *   projectKeys.all         ["projects"]                    the picker's list
 *   projectKeys.detail(id)  ["project", id]                 one project
 *   documentKeys.list(id)   ["project", id, "documents"]    nests underneath
 *   phaseKeys.list(id)      ["project", id, "phases"]       nests underneath
 *
 * so invalidating `detail(id)` correctly takes every sub-resource of that
 * project with it, and nothing else.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Project, ProjectBasics, ProjectBasicsPatch } from "@gb/contracts";
import { api, errorMessage } from "@/api/client";

export const projectKeys = {
  all: ["projects"] as const,
  detail: (id: string) => ["project", id] as const,
};

/** Every project in the tenant, newest first. Archived ones included. */
export function useProjects() {
  return useQuery({
    queryKey: projectKeys.all,
    queryFn: async (): Promise<Project[]> => {
      const res = await api.projects.list();
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.projects;
    },
  });
}

/**
 * One project by id.
 *
 * A 404 is surfaced as a typed error rather than `null`: an unknown or foreign
 * id is a *wrong URL*, which the layout must say out loud, and it is a
 * different thing from a workspace that simply has no projects yet.
 */
export class ProjectNotFoundError extends Error {
  constructor() {
    super("Project not found");
    this.name = "ProjectNotFoundError";
  }
}

export function useProject(projectId: string | undefined) {
  return useQuery({
    queryKey: projectKeys.detail(projectId ?? ""),
    enabled: Boolean(projectId),
    // A missing project will not appear by trying again, so a 404 is final —
    // retrying just delays the "not found" screen by several seconds.
    //
    // `failureCount` is bounded, which it must be: a predicate that only tests
    // the error type returns `true` forever on a network failure, so the query
    // never settles and the layout sits on "Opening project…" indefinitely
    // instead of offering the Try again button it has.
    retry: (failureCount, error) =>
      !(error instanceof ProjectNotFoundError) && failureCount < 2,
    queryFn: async (): Promise<Project> => {
      const res = await api.projects.get({ params: { id: projectId! } });
      if (res.status === 404) throw new ProjectNotFoundError();
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.project;
    },
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ProjectBasics): Promise<Project> => {
      const res = await api.projects.create({ body: input });
      if (res.status !== 201) throw new Error(errorMessage(res));
      return res.body.project;
    },
    onSuccess: (project) => {
      // Seed the cache directly as well as invalidating: the wizard navigates
      // to step 2 immediately, and a refetch round trip would flash an empty
      // step behind the transition.
      queryClient.setQueryData(projectKeys.detail(project.id), project);
      void queryClient.invalidateQueries({ queryKey: projectKeys.all });
    },
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; patch: ProjectBasicsPatch }): Promise<Project> => {
      const res = await api.projects.patch({
        params: { id: vars.id },
        body: vars.patch,
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.project;
    },
    onSuccess: (project) => {
      queryClient.setQueryData(projectKeys.detail(project.id), project);
      // The name and status are on every picker row.
      void queryClient.invalidateQueries({ queryKey: projectKeys.all });
    },
  });
}

/** Advance the resume cursor. The server keeps `max(stored, incoming)`. */
export function useSetSetupStep() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; step: number }): Promise<Project> => {
      const res = await api.projects.setSetupStep({
        params: { id: vars.id },
        body: { step: vars.step },
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.project;
    },
    onSuccess: (project) => {
      queryClient.setQueryData(projectKeys.detail(project.id), project);
    },
  });
}

export function useActivateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<Project> => {
      const res = await api.projects.activate({ params: { id }, body: {} });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.project;
    },
    onSuccess: (project) => {
      queryClient.setQueryData(projectKeys.detail(project.id), project);
      // Everything under this project reads differently once it is live. This
      // used to be a bare `invalidateQueries()` that cleared the whole app,
      // only because the old key shape gave it no way to be more precise.
      void queryClient.invalidateQueries({ queryKey: projectKeys.detail(project.id) });
      void queryClient.invalidateQueries({ queryKey: projectKeys.all });
    },
  });
}

export function useArchiveProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<Project> => {
      const res = await api.projects.archive({ params: { id }, body: {} });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.project;
    },
    onSuccess: (project) => {
      queryClient.setQueryData(projectKeys.detail(project.id), project);
      void queryClient.invalidateQueries({ queryKey: projectKeys.all });
    },
  });
}

export function useUnarchiveProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<Project> => {
      const res = await api.projects.unarchive({ params: { id }, body: {} });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.project;
    },
    onSuccess: (project) => {
      queryClient.setQueryData(projectKeys.detail(project.id), project);
      void queryClient.invalidateQueries({ queryKey: projectKeys.all });
    },
  });
}
